import { useAuth } from "@clerk/clerk-react";
import { getHerdQueryOptions } from "@packages/api-client/hooks/livestockController";
import { LivestockMeasurementType } from "@packages/shared-types/livestock";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	CalendarDays,
	Check,
	ChevronUp,
	Loader2,
	Plus,
	Scale,
	Search,
	Trash2,
	X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateMeasurementsBatch } from "@/api/livestock";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { OfflineUnavailable } from "@/components/OfflineUnavailable";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import { useOfflineSession } from "@/context/OfflineSessionContext";
import { useSyncStatus } from "@/context/SyncStatusContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { getOfflineHerdRow } from "@/hooks/useHerdsListData";
import {
	type RosterDisplayAnimal,
	useWeighingRoster,
} from "@/hooks/useWeighingRoster";
import { isOfflineQueued } from "@/lib/offline/defineOfflineMutation";
import { translateBackendError } from "@/utils/errorTranslation";
import { cn } from "@/utils/utils";
import { translateBatchItemError } from "../../-components/batchErrors";
import {
	addWeighingEntry,
	createWeighingDraft,
	MAX_WEIGHT_KG,
	MIN_WEIGHT_KG,
	matchTag,
	parseWeighingDraft,
	parseWeightInput,
	pruneWeighingEntries,
	removeWeighingEntry,
	restoreWeighingEntry,
	serializeWeighingDraft,
	todayLocalIsoDate,
	totalWeightKg,
	WEIGHING_SELECTION_SCOPE_KEY,
	type WeighingDraft,
	type WeighingEntry,
	type WeighingRosterAnimal,
	weighedWeightByAnimalId,
	weighingDraftKey,
	weightBoundsError,
} from "./-components/weighingSession";

type WeighingSearch = {
	/** Scopes the tag roster to one herd (entry from herd detail). */
	herd?: string;
	/** Reads the animal-id scope stashed by the animals-list bulk action. */
	scope?: "selection";
};

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/weighing/",
)({
	// The chute is exactly where connectivity dies: the roster reads from the
	// prepared offline dataset and the batch commit queues (§ offline plan).
	staticData: { offline: "ready" },
	validateSearch: (search: Record<string, unknown>): WeighingSearch => ({
		herd: typeof search.herd === "string" ? search.herd : undefined,
		scope: search.scope === "selection" ? "selection" : undefined,
	}),
	component: WeighingSessionPage,
});

function readSelectionScope(): string[] | null {
	try {
		const parsed = JSON.parse(
			sessionStorage.getItem(WEIGHING_SELECTION_SCOPE_KEY) ?? "null",
		) as { animalIds?: unknown } | null;
		if (
			parsed &&
			Array.isArray(parsed.animalIds) &&
			parsed.animalIds.length > 0 &&
			parsed.animalIds.every((id) => typeof id === "string")
		) {
			return parsed.animalIds as string[];
		}
	} catch {
		// corrupt/unavailable storage — fall back to the whole-farm roster.
	}
	return null;
}

/**
 * Weighing session ("Pesar lote") — the chute/curral flow: sticky tag input →
 * weight → Enter → row appended → focus back on the tag. The roster the tag
 * matches against is the herd (`?herd=`), the bulk-action selection
 * (`?scope=selection`), or the whole farm. The session autosaves to
 * localStorage (AnimalForm draft pattern) so an accidental navigation — or a
 * detour to create an unknown animal — never loses captured rows, and commits
 * as ONE batched backend call with an inline per-tag failure report.
 *
 * Mobile is the primary scene (one hand at the chute, sun on the screen), so
 * the layout runs on a strict sticky-region budget: one compact capture card
 * on top (header row folded in, labels carried by placeholders/aria), one
 * single-row commit bar on the bottom, and the roster behind a bottom sheet.
 * The org shell hides its bottom tab bar on this route (focused session).
 */
function WeighingSessionPage() {
	const { orgId, establishmentId } = Route.useParams();
	const { herd: herdId, scope } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const uid = useId();
	const { formatDateOnly } = useDateFormatter();

	// User-scoped draft key (§3.4): without a signed-in user id there is no key,
	// so the session simply doesn't persist (another user on a shared browser can
	// never restore someone else's captures).
	//
	// On an offline cold boot the app runs in bypass mode — Clerk is never
	// loaded, so `useAuth().userId` is null and the session would autosave
	// nowhere, in the one scenario (cold boot at the chute) this screen exists
	// for. The cached offline session carries the same Clerk user id, so the key
	// is identical across online and bypass boots.
	const { userId: clerkUserId } = useAuth();
	const { offlineSession } = useOfflineSession();
	const userId = clerkUserId ?? offlineSession?.userId ?? null;
	const draftKeyValue = userId
		? weighingDraftKey(userId, orgId, establishmentId)
		: null;
	const [draft, setDraft] = useState<WeighingDraft>(() => {
		if (!draftKeyValue) return createWeighingDraft(establishmentId);
		try {
			const existing = parseWeighingDraft(localStorage.getItem(draftKeyValue));
			if (existing && existing.establishmentId === establishmentId) {
				return existing;
			}
		} catch {
			// storage unavailable — start clean.
		}
		return createWeighingDraft(establishmentId);
	});

	// Autosave the session so nothing is lost between captures; an empty session
	// leaves no orphan draft behind. Persisted in the §3.4 envelope so sign-out
	// cleanup and the TTL sweep recognise it.
	useEffect(() => {
		if (!draftKeyValue) return;
		try {
			if (draft.entries.length > 0) {
				localStorage.setItem(draftKeyValue, serializeWeighingDraft(draft));
			} else {
				localStorage.removeItem(draftKeyValue);
			}
		} catch {
			// ignore storage failures (private mode / quota).
		}
	}, [draft, draftKeyValue]);

	const [selectionIds] = useState<string[] | null>(() =>
		scope === "selection" ? readSelectionScope() : null,
	);

	const { online } = useSyncStatus();
	const queryClient = useQueryClient();

	const {
		animals: roster,
		isLoading: rosterLoading,
		isPrepared,
	} = useWeighingRoster({ orgId, establishmentId, herdId, selectionIds });

	const herdQuery = useQuery({
		...getHerdQueryOptions(orgId, herdId ?? ""),
		enabled: !!herdId,
	});
	// Offline the herd query pauses; the prepared herds dataset carries the label.
	const herdName =
		herdQuery.data?.name ??
		(herdId
			? getOfflineHerdRow(queryClient, orgId, establishmentId, herdId)?.name
			: undefined);

	// animalId → weight captured this session; drives the roster's weighed/pending split.
	const weighedByAnimalId = useMemo(
		() => weighedWeightByAnimalId(draft),
		[draft],
	);
	const rosterDoneCount = useMemo(
		() => roster.filter((animal) => weighedByAnimalId.has(animal.id)).length,
		[roster, weighedByAnimalId],
	);

	const [tagQuery, setTagQuery] = useState("");
	const [candidate, setCandidate] = useState<WeighingRosterAnimal | null>(null);
	const [weightText, setWeightText] = useState("");
	// Last appended row — shown as the idle-state confirmation under the inputs,
	// since the session list itself may be scrolled off-screen on mobile.
	const [lastAdded, setLastAdded] = useState<{
		tagId: string;
		weightKg: number;
	} | null>(null);
	// entryId → translated reason, from the last partial commit.
	const [failures, setFailures] = useState<Record<string, string>>({});
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	// "Save weighings" first opens a review modal; the commit fires on confirm.
	const [confirmSave, setConfirmSave] = useState(false);
	const [rosterOpen, setRosterOpen] = useState(false);
	const tagInputRef = useRef<HTMLInputElement>(null);
	const weightInputRef = useRef<HTMLInputElement>(null);
	// Whether the roster sheet closed because an animal was picked — steers the
	// close-focus to the weight input instead of back to the sheet trigger.
	const sheetPickedRef = useRef(false);

	const match = useMemo(() => matchTag(roster, tagQuery), [roster, tagQuery]);
	const lockedAnimal = candidate ?? match.exact;
	// Weight guard: a parseable number outside the plausible band surfaces an
	// inline error and never becomes a row — only `validWeight` can be captured.
	const parsedWeight = parseWeightInput(weightText);
	const weightError =
		parsedWeight === null ? null : weightBoundsError(parsedWeight);
	const validWeight = weightError === null ? parsedWeight : null;
	const trimmedTag = tagQuery.trim();
	const showUnknownTag =
		trimmedTag.length > 0 &&
		!lockedAnimal &&
		match.suggestions.length === 0 &&
		!rosterLoading;
	// Offline, "create the animal" still works (`animals:create` queues) but the
	// new animal has no server id until sync, so it cannot join this batch —
	// the copy has to say "register now, weigh after sync" instead (R2).
	const unknownTagCopy = online
		? t("livestock.weighing.unknownTag")
		: t("livestock.weighing.unknownTagOffline");

	const pickAnimal = (animal: WeighingRosterAnimal) => {
		setCandidate(animal);
		setTagQuery(animal.tagId);
		weightInputRef.current?.focus();
	};

	const pickFromSheet = (animal: WeighingRosterAnimal) => {
		sheetPickedRef.current = true;
		setRosterOpen(false);
		pickAnimal(animal);
	};

	const handleSheetCloseFocus = (event: Event) => {
		if (!sheetPickedRef.current) return;
		sheetPickedRef.current = false;
		event.preventDefault();
		weightInputRef.current?.focus();
	};

	const addRow = () => {
		if (!lockedAnimal || validWeight === null) return;
		setDraft((current) =>
			addWeighingEntry(current, {
				animalId: lockedAnimal.id,
				tagId: lockedAnimal.tagId,
				weightKg: validWeight,
			}),
		);
		setLastAdded({ tagId: lockedAnimal.tagId, weightKg: validWeight });
		setTagQuery("");
		setCandidate(null);
		setWeightText("");
		tagInputRef.current?.focus();
	};

	const removeRow = (entry: WeighingEntry) => {
		const index = draft.entries.findIndex((row) => row.id === entry.id);
		setDraft((current) => removeWeighingEntry(current, entry.id));
		// A gloved mis-tap must not silently lose a capture — offer undo.
		toast(t("livestock.weighing.entryRemoved", { tag: entry.tagId }), {
			action: {
				label: t("livestock.weighing.undo"),
				onClick: () =>
					setDraft((current) => restoreWeighingEntry(current, entry, index)),
			},
		});
	};

	const batchMutation = useCreateMeasurementsBatch();

	/** The livestock hub — `ready` offline, unlike herd detail. */
	const navigateToHub = () => {
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm: establishmentId },
		});
	};

	const navigateAfterSave = () => {
		if (herdId) {
			navigate({
				to: "/app/org/$orgId/livestock/$establishmentId/herds/$herdId",
				params: { orgId, establishmentId, herdId },
			});
			return;
		}
		navigateToHub();
	};

	const commitSession = () => {
		if (draft.entries.length === 0 || batchMutation.isPending) return;
		// Entries render newest-first; commit them in capture order.
		const orderedEntries = [...draft.entries].reverse();
		batchMutation.mutate(
			{
				organizationId: orgId,
				data: {
					establishmentId,
					measurementType: LivestockMeasurementType.WEIGHT,
					measurementDate: draft.date,
					unit: "kg",
					items: orderedEntries.map((entry) => ({
						animalId: entry.animalId,
						numericValue: entry.weightKg,
					})),
				},
			},
			{
				onSuccess: (result) => {
					// Queued offline: there is no per-item report to read yet, and the
					// payload carries `draft.date` verbatim, so a session captured
					// Tuesday and synced Thursday still records Tuesday. The
					// "saved offline" toast comes from the central MutationCache.
					if (isOfflineQueued(result)) {
						setFailures({});
						setDraft(createWeighingDraft(establishmentId));
						// Not navigateAfterSave(): with `?herd=` that lands on herd detail,
						// which offline needs a prepared *herds* dataset to render anything
						// but the "prepare in the Offline Center" panel. The hub is the one
						// destination that always renders offline.
						navigateToHub();
						return;
					}

					const failedByEntry: Record<string, string> = {};
					const succeededIds = new Set<string>();
					for (const item of result.results) {
						const entry = orderedEntries[item.index];
						if (!entry) continue;
						if (item.ok) succeededIds.add(entry.id);
						else
							failedByEntry[entry.id] = translateBatchItemError(item.error, t);
					}

					if (result.failedCount === 0) {
						toast.success(
							t("livestock.weighing.saveSuccess", {
								count: result.succeededCount,
							}),
						);
						setFailures({});
						setDraft(createWeighingDraft(establishmentId));
						navigateAfterSave();
						return;
					}

					// Partial: keep exactly the failed rows for retry, report inline.
					toast.error(
						result.succeededCount === 0
							? t("livestock.weighing.saveAllFailed")
							: t("livestock.weighing.savePartial", {
									success: result.succeededCount,
									failed: result.failedCount,
								}),
					);
					setFailures(failedByEntry);
					setDraft((current) => pruneWeighingEntries(current, succeededIds));
				},
				onError: (error) => {
					toast.error(
						translateBackendError((error as Error).message, t) ||
							t("errors.api.INTERNAL_ERROR"),
					);
				},
			},
		);
	};

	const discardSession = () => {
		setDraft(createWeighingDraft(establishmentId));
		setFailures({});
		setLastAdded(null);
		setConfirmDiscard(false);
		tagInputRef.current?.focus();
	};

	const scopeLabel = herdId
		? t("livestock.weighing.scopeHerd", { name: herdName ?? "…" })
		: selectionIds
			? t("livestock.weighing.scopeSelection", { count: selectionIds.length })
			: t("livestock.weighing.scopeFarm");

	const entryCount = draft.entries.length;
	const total = totalWeightKg(draft);
	const rosterTotal = roster.length;

	const isToday = draft.date === todayLocalIsoDate();
	const dateLabel = isToday
		? t("livestock.weighing.dateToday")
		: formatDateOnly(new Date(draft.date).getTime());

	// The capture feedback is visual-only without this: announce lock /
	// suggestion count / unknown tag / last capture to screen readers.
	const liveStatus = lockedAnimal
		? lockedAnimal.tagId
		: match.suggestions.length > 0
			? t("livestock.weighing.suggestionCount", {
					count: match.suggestions.length,
				})
			: showUnknownTag
				? unknownTagCopy
				: lastAdded
					? t("livestock.weighing.lastAdded", {
							tag: lastAdded.tagId,
							weight: lastAdded.weightKg.toLocaleString(),
						})
					: "";

	// Offline without a prepared animals dataset there is no roster to match tags
	// against, so the capture flow can't work at all. Same panel the route guard
	// used to show (the AnimalDetail precedent), pointing at the Offline Center —
	// not a bespoke banner. Any captured draft stays in localStorage untouched.
	if (!online && !isPrepared) {
		return (
			<div className="mx-auto flex min-h-full w-full max-w-2xl flex-col lg:max-w-5xl">
				<div className="flex items-center gap-1 p-3">
					<button
						type="button"
						onClick={() => window.history.back()}
						aria-label={t("common.back")}
						data-testid="back-button"
						className="-ml-1.5 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-base-content/70 transition-colors hover:bg-base-200 active:bg-base-200"
					>
						<ArrowLeft className="h-5 w-5" />
					</button>
					<h1 className="truncate text-base font-bold" data-testid="page-title">
						{t("livestock.weighing.title")}
					</h1>
				</div>
				<OfflineUnavailable variant="preparable" />
			</div>
		);
	}

	return (
		<div className="mx-auto flex min-h-full w-full max-w-2xl flex-col lg:max-w-5xl">
			{/* Two-pane on desktop: the capture→list→commit workflow in the left
			    column, the session roster as a sticky right rail. On mobile it all
			    stacks; the roster lives behind the pinned progress line (bottom
			    sheet), so the session list owns the remaining vertical space. */}
			<div className="flex flex-1 flex-col gap-3 lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start lg:gap-4">
				{/* Capture card — sticky so the input pair stays under the thumb while
				    the session list grows. The page header is folded into it: back +
				    title + scope on one compact row, discard demoted to an icon. */}
				<div className="sticky top-0 z-10 rounded-xl border border-base-300 bg-base-100 p-3 shadow-sm lg:col-start-1 lg:row-start-1">
					<div className="mb-2.5 flex items-center gap-1">
						<button
							type="button"
							onClick={() => window.history.back()}
							aria-label={t("common.back")}
							data-testid="back-button"
							className="-ml-1.5 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-base-content/70 transition-colors hover:bg-base-200 active:bg-base-200"
						>
							<ArrowLeft className="h-5 w-5" />
						</button>
						<div className="min-w-0 flex-1">
							<h1
								className="truncate text-base font-bold leading-tight"
								data-testid="page-title"
							>
								{t("livestock.weighing.title")}
							</h1>
							<p className="truncate text-xs text-base-content/60">
								{scopeLabel}
							</p>
						</div>
						{entryCount > 0 && (
							<button
								type="button"
								onClick={() => setConfirmDiscard(true)}
								aria-label={t("livestock.weighing.discard")}
								className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-base-content/50 transition-colors hover:bg-base-200 hover:text-error active:bg-base-200"
							>
								<Trash2 className="h-5 w-5" />
							</button>
						)}
					</div>

					<div className="flex items-center gap-2">
						<Input
							id={`${uid}-tag`}
							ref={tagInputRef}
							autoFocus
							enterKeyHint="next"
							autoComplete="off"
							aria-label={t("livestock.weighing.tagLabel")}
							className="h-12 min-w-0 flex-[3] text-base"
							value={tagQuery}
							onChange={(e) => {
								setTagQuery(e.target.value);
								setCandidate(null);
							}}
							onKeyDown={(e) => {
								if (e.key !== "Enter") return;
								e.preventDefault();
								if (lockedAnimal) {
									weightInputRef.current?.focus();
								} else if (match.suggestions.length === 1) {
									pickAnimal(match.suggestions[0]);
								}
							}}
							placeholder={t("livestock.weighing.tagPlaceholder")}
						/>
						<div className="relative min-w-0 flex-[2]">
							<Input
								id={`${uid}-weight`}
								ref={weightInputRef}
								inputMode="decimal"
								enterKeyHint="done"
								autoComplete="off"
								aria-label={t("livestock.weighing.weightLabel")}
								aria-invalid={weightError !== null}
								className={cn(
									"h-12 w-full pr-9 text-lg font-medium tabular-nums md:text-lg",
									weightError && "border-error focus-visible:ring-error",
								)}
								value={weightText}
								onChange={(e) => setWeightText(e.target.value)}
								onKeyDown={(e) => {
									if (e.key !== "Enter") return;
									e.preventDefault();
									addRow();
								}}
								placeholder={t("livestock.weighing.weightPlaceholder")}
							/>
							<span
								aria-hidden="true"
								className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-base-content/50"
							>
								kg
							</span>
						</div>
						<Button
							onClick={addRow}
							disabled={!lockedAnimal || validWeight === null}
							aria-label={t("livestock.weighing.add")}
							className="h-12 w-12 shrink-0"
						>
							<Plus className="h-5 w-5" />
						</Button>
					</div>

					{/* Out-of-band weight — blocks the capture until corrected. */}
					{weightError && (
						<p role="alert" className="mt-2 text-sm font-medium text-error">
							{weightError === "belowMin"
								? t("livestock.weighing.weightBelowMin", {
										min: MIN_WEIGHT_KG,
									})
								: t("livestock.weighing.weightAboveMax", {
										max: MAX_WEIGHT_KG,
									})}
						</p>
					)}

					{/* Resolution line: locked animal, suggestions, the unknown-tag
					    shortcut, or the last-capture confirmation — one at a time,
					    right under the tag input. */}
					{lockedAnimal ? (
						<p className="mt-2 flex items-center gap-1.5 text-sm text-base-content/70">
							<Scale className="h-3.5 w-3.5 shrink-0 text-primary" />
							<span className="font-medium text-base-content">
								{lockedAnimal.tagId}
							</span>
						</p>
					) : match.suggestions.length > 0 ? (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{match.suggestions.map((animal) => (
								<button
									key={animal.id}
									type="button"
									onClick={() => pickAnimal(animal)}
									className="rounded-full border border-base-300 bg-base-100 px-4 py-2 text-sm transition-colors hover:border-primary/50 hover:bg-base-200 active:bg-base-200 sm:py-1.5"
								>
									<span className="font-medium">{animal.tagId}</span>
								</button>
							))}
						</div>
					) : showUnknownTag ? (
						<div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-warning/10 px-3 py-2">
							<p className="text-sm text-base-content/80">{unknownTagCopy}</p>
							<Button
								variant="outline"
								size="sm"
								onClick={() =>
									navigate({
										to: "/app/org/$orgId/livestock/$establishmentId/animals/new",
										params: { orgId, establishmentId },
										search: { tag: trimmedTag },
									})
								}
							>
								{t("livestock.weighing.createAnimal", { tag: trimmedTag })}
							</Button>
						</div>
					) : rosterLoading && trimmedTag ? (
						<p className="mt-2 flex items-center gap-2 text-sm text-base-content/60">
							<Loader2 className="h-3.5 w-3.5 animate-spin" />
							{t("common.loading")}
						</p>
					) : lastAdded ? (
						<p className="mt-2 flex items-center gap-1.5 text-sm text-base-content/70">
							<Check className="h-3.5 w-3.5 shrink-0 text-success" />
							{t("livestock.weighing.lastAdded", {
								tag: lastAdded.tagId,
								weight: lastAdded.weightKg.toLocaleString(),
							})}
						</p>
					) : null}
					<span className="sr-only" aria-live="polite">
						{liveStatus}
					</span>

					{/* Pinned roster line — the mobile stand-in for the desktop rail:
					    progress at a glance, tap for the bottom-sheet checklist. */}
					<button
						type="button"
						onClick={() => setRosterOpen(true)}
						aria-label={t("livestock.weighing.rosterToggle")}
						className="mt-2.5 block w-full border-t border-base-300 pt-2 text-left lg:hidden"
					>
						<span className="flex items-center justify-between gap-2">
							<span className="text-xs font-medium text-base-content/70">
								{t("livestock.weighing.rosterTitle")}
							</span>
							<span className="flex items-center gap-1 text-xs text-base-content/60">
								{t("livestock.weighing.rosterProgress", {
									done: rosterDoneCount,
									total: rosterTotal,
								})}
								<ChevronUp className="h-3.5 w-3.5 shrink-0 text-base-content/50" />
							</span>
						</span>
						{rosterTotal > 0 && (
							<Progress
								value={rosterDoneCount}
								max={rosterTotal}
								className="mt-1.5 h-1.5"
								aria-hidden="true"
							/>
						)}
					</button>
				</div>

				{/* Session roster, desktop: every animal in scope as a sticky right
				    rail, split weighed / to-weigh. */}
				<SessionRosterRail
					animals={roster}
					weighedByAnimalId={weighedByAnimalId}
					lockedAnimalId={lockedAnimal?.id}
					isLoading={rosterLoading}
					doneCount={rosterDoneCount}
					onPick={pickAnimal}
					className="hidden lg:sticky lg:top-0 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:flex"
				/>

				{/* Session list — newest capture on top. */}
				<div className="flex-1 lg:col-start-1 lg:row-start-2">
					{entryCount === 0 ? (
						<div className="rounded-xl border border-dashed border-base-300 px-4 py-10 text-center">
							<Scale className="mx-auto h-8 w-8 text-base-content/30" />
							<p className="mt-2 text-sm font-medium">
								{t("livestock.weighing.empty")}
							</p>
							<p className="mt-1 text-xs text-base-content/60">
								{t("livestock.weighing.emptyHint")}
							</p>
						</div>
					) : (
						<ul className="divide-y rounded-xl border border-base-300 bg-base-100">
							{draft.entries.map((entry) => {
								const failure = failures[entry.id];
								return (
									<li
										key={entry.id}
										className="flex items-center gap-3 px-3 py-2.5"
									>
										<div className="min-w-0 flex-1">
											<div className="flex items-baseline gap-2">
												<span className="text-sm font-medium text-primary">
													{entry.tagId}
												</span>
											</div>
											{failure && (
												<p className="mt-0.5 text-xs text-error">{failure}</p>
											)}
										</div>
										<span className="shrink-0 text-base font-semibold tabular-nums">
											{entry.weightKg.toLocaleString()} kg
										</span>
										<button
											type="button"
											onClick={() => removeRow(entry)}
											aria-label={t("livestock.weighing.removeEntry")}
											className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-base-content/50 transition-colors hover:bg-base-200 hover:text-error active:bg-base-200 sm:h-9 sm:w-9"
										>
											<X className="h-4 w-4" />
										</button>
									</li>
								);
							})}
						</ul>
					)}
				</div>

				{/* Commit bar — sticky so "save the session" survives a long list.
				    One row: date chip (native picker underneath) + save. Bleeds to
				    the screen edge on mobile so nothing peeks around it. */}
				<div className="sticky bottom-0 z-10 -mx-3 border-t border-base-300 bg-base-100 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-2.5 lg:col-start-1 lg:row-start-3 lg:mx-0 lg:px-1">
					<div className="flex items-stretch gap-2">
						<label
							htmlFor={`${uid}-date`}
							className="relative flex h-12 shrink-0 cursor-pointer items-center gap-1.5 rounded-lg border border-base-300 bg-base-100 px-3 text-sm font-medium transition-colors focus-within:border-primary hover:bg-base-200"
						>
							<CalendarDays className="h-4 w-4 shrink-0 text-base-content/60" />
							<span className="whitespace-nowrap">{dateLabel}</span>
							<input
								id={`${uid}-date`}
								type="date"
								aria-label={t("livestock.weighing.date")}
								className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
								value={draft.date}
								onChange={(e) => {
									const next = e.target.value;
									if (!next) return;
									setDraft((current) => ({ ...current, date: next }));
								}}
							/>
						</label>
						<Button
							onClick={() => setConfirmSave(true)}
							disabled={entryCount === 0 || batchMutation.isPending}
							className="h-12 min-w-0 flex-1 text-base"
						>
							{batchMutation.isPending && (
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
							)}
							{t("livestock.weighing.save", { count: entryCount })}
						</Button>
					</div>
					{entryCount > 0 && (
						<p className="mt-1.5 truncate text-xs text-base-content/60">
							{t("livestock.weighing.totalWeight", {
								total: total.toLocaleString(),
							})}
							{draftKeyValue ? ` · ${t("livestock.weighing.autosaved")}` : null}
						</p>
					)}
				</div>
			</div>

			{/* Mobile roster — bottom sheet: thumb-reachable, own scroll, and
			    tap-to-pick lands focus on the weight input. */}
			<Sheet open={rosterOpen} onOpenChange={setRosterOpen}>
				<SheetContent
					side="bottom"
					className="max-h-[85dvh] gap-0 rounded-t-2xl p-0 lg:hidden"
					onCloseAutoFocus={handleSheetCloseFocus}
				>
					<SheetHeader className="shrink-0 gap-1 border-b border-base-300 p-4 pb-3 pr-12">
						<SheetTitle className="text-base">
							{t("livestock.weighing.rosterTitle")}
						</SheetTitle>
						<SheetDescription className="text-xs">
							{t("livestock.weighing.rosterProgress", {
								done: rosterDoneCount,
								total: rosterTotal,
							})}
						</SheetDescription>
						{rosterTotal > 0 && (
							<Progress
								value={rosterDoneCount}
								max={rosterTotal}
								className="mt-1 h-1.5"
								aria-hidden="true"
							/>
						)}
					</SheetHeader>
					<RosterBody
						animals={roster}
						weighedByAnimalId={weighedByAnimalId}
						lockedAnimalId={lockedAnimal?.id}
						isLoading={rosterLoading}
						onPick={pickFromSheet}
						className="pb-[max(0.5rem,env(safe-area-inset-bottom))]"
					/>
				</SheetContent>
			</Sheet>

			<ConfirmDeleteDialog
				open={confirmDiscard}
				title={t("livestock.weighing.discard")}
				description={t("livestock.weighing.discardConfirm", {
					count: entryCount,
				})}
				onCancel={() => setConfirmDiscard(false)}
				onConfirm={discardSession}
				isPending={false}
				confirmLabel={t("livestock.weighing.discard")}
			/>

			{/* Save review — the captured rows one last time before the commit, so
			    a fat-fingered weight is caught before it lands in the records. */}
			<AlertDialog open={confirmSave} onOpenChange={setConfirmSave}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("livestock.weighing.confirmSaveTitle", { count: entryCount })}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("livestock.weighing.confirmSaveDescription")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<ul className="max-h-[45dvh] divide-y overflow-y-auto rounded-lg border border-base-300">
						{draft.entries.map((entry) => (
							<li
								key={entry.id}
								className="flex items-center justify-between gap-3 px-3 py-2"
							>
								<span className="min-w-0 truncate text-sm font-medium text-primary">
									{entry.tagId}
								</span>
								<span className="shrink-0 text-sm font-semibold tabular-nums">
									{entry.weightKg.toLocaleString()} kg
								</span>
							</li>
						))}
					</ul>
					<p className="text-sm text-base-content/70">
						{dateLabel} ·{" "}
						{t("livestock.weighing.totalWeight", {
							total: total.toLocaleString(),
						})}
					</p>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setConfirmSave(false)}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setConfirmSave(false);
								commitSession();
							}}
						>
							{t("livestock.weighing.save", { count: entryCount })}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}

/** Above this count the roster asks for a filter before rendering rows,
    and the filter box always shows — keeps a whole-farm roster snappy. */
const ROSTER_FILTER_THRESHOLD = 40;

type RosterBodyProps = {
	animals: RosterDisplayAnimal[];
	weighedByAnimalId: Map<string, number>;
	lockedAnimalId?: string;
	isLoading: boolean;
	onPick: (animal: RosterDisplayAnimal) => void;
	className?: string;
};

/**
 * The roster checklist itself — filter box, the still-to-weigh animals (tap to
 * load the capture input), and the ones already weighed this session. Shared
 * between the desktop rail and the mobile bottom sheet; presentational, all
 * session state is owned by the page.
 */
function RosterBody({
	animals,
	weighedByAnimalId,
	lockedAnimalId,
	isLoading,
	onPick,
	className,
}: RosterBodyProps) {
	const { t } = useTranslation();
	const { formatDateOnly } = useDateFormatter();
	const [filter, setFilter] = useState("");

	const total = animals.length;
	const needle = filter.trim().toLowerCase();
	const { pending, weighed } = useMemo(() => {
		const matched = needle
			? animals.filter((a) => a.tagId.toLowerCase().includes(needle))
			: animals;
		return {
			pending: matched.filter((a) => !weighedByAnimalId.has(a.id)),
			weighed: matched.filter((a) => weighedByAnimalId.has(a.id)),
		};
	}, [animals, weighedByAnimalId, needle]);

	const showFilter = total > 8;
	// Whole-farm rosters can be huge; require a filter before painting the rows.
	const gateOnFilter = total > ROSTER_FILTER_THRESHOLD && needle.length === 0;

	return (
		<div className={cn("flex min-h-0 flex-1 flex-col", className)}>
			{showFilter && (
				<div className="relative shrink-0 p-3">
					<Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-base-content/40" />
					<Input
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						placeholder={t("livestock.weighing.rosterSearchPlaceholder")}
						autoComplete="off"
						className="pl-9"
					/>
				</div>
			)}

			<div className="min-h-0 flex-1 overflow-y-auto px-1 pb-2">
				{isLoading && total === 0 ? (
					<p className="flex items-center gap-2 px-3 py-6 text-sm text-base-content/60">
						<Loader2 className="h-4 w-4 animate-spin" />
						{t("common.loading")}
					</p>
				) : total === 0 ? (
					<p className="px-3 py-6 text-center text-sm text-base-content/60">
						{t("livestock.weighing.rosterEmpty")}
					</p>
				) : gateOnFilter ? (
					<p className="px-3 py-6 text-center text-sm text-base-content/60">
						{t("livestock.weighing.rosterFilterHint", { count: total })}
					</p>
				) : pending.length === 0 && weighed.length === 0 ? (
					<p className="px-3 py-6 text-center text-sm text-base-content/60">
						{t("livestock.weighing.rosterNoMatch")}
					</p>
				) : (
					<>
						{pending.length > 0 && (
							<RosterGroup
								label={t("livestock.weighing.rosterToWeigh")}
								count={pending.length}
							>
								{pending.map((animal) => (
									<button
										key={animal.id}
										type="button"
										onClick={() => onPick(animal)}
										aria-label={t("livestock.weighing.rosterSelect", {
											tag: animal.tagId,
										})}
										className={cn(
											"flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-colors hover:bg-base-200 active:bg-base-200 sm:py-2",
											lockedAnimalId === animal.id && "bg-primary/10",
										)}
									>
										<span className="h-2 w-2 shrink-0 rounded-full border border-base-content/30" />
										<span className="min-w-0 flex-1">
											<span className="block truncate text-sm font-medium">
												{animal.tagId}
											</span>
											{animal.herdName && (
												<span className="block truncate text-xs text-base-content/60">
													{animal.herdName}
												</span>
											)}
										</span>
										{animal.lastWeightKg != null && (
											<span className="shrink-0 text-right">
												<span className="block text-xs tabular-nums text-base-content/55">
													{t("livestock.weighing.rosterLastWeight", {
														weight: animal.lastWeightKg.toLocaleString(),
													})}
												</span>
												{animal.lastWeightDate && (
													<span className="block text-[11px] tabular-nums text-base-content/45">
														{formatDateOnly(
															new Date(animal.lastWeightDate).getTime(),
														)}
													</span>
												)}
											</span>
										)}
									</button>
								))}
							</RosterGroup>
						)}
						{weighed.length > 0 && (
							<RosterGroup
								label={t("livestock.weighing.rosterWeighed")}
								count={weighed.length}
							>
								{weighed.map((animal) => (
									<div
										key={animal.id}
										className="flex items-center gap-2.5 rounded-lg px-2.5 py-2"
									>
										<Check className="h-4 w-4 shrink-0 text-success" />
										<span className="min-w-0 flex-1">
											<span className="block truncate text-sm font-medium">
												{animal.tagId}
											</span>
											{animal.herdName && (
												<span className="block truncate text-xs text-base-content/60">
													{animal.herdName}
												</span>
											)}
										</span>
										<Badge variant="success" className="shrink-0 tabular-nums">
											{(weighedByAnimalId.get(animal.id) ?? 0).toLocaleString()}{" "}
											kg
										</Badge>
									</div>
								))}
							</RosterGroup>
						)}
					</>
				)}
			</div>
		</div>
	);
}

type SessionRosterRailProps = Omit<RosterBodyProps, "className"> & {
	doneCount: number;
	className?: string;
};

/**
 * "Animals in session", desktop: the roster as an always-expanded sticky right
 * rail with its own progress header. Mobile uses the bottom sheet instead.
 */
function SessionRosterRail({
	animals,
	weighedByAnimalId,
	lockedAnimalId,
	isLoading,
	doneCount,
	onPick,
	className,
}: SessionRosterRailProps) {
	const { t } = useTranslation();
	const total = animals.length;

	return (
		<aside
			className={cn(
				"flex-col overflow-hidden rounded-xl border border-base-300 bg-base-100 lg:max-h-[calc(100vh-1rem)]",
				className,
			)}
		>
			<div className="shrink-0 p-3">
				<h2 className="text-sm font-semibold">
					{t("livestock.weighing.rosterTitle")}
				</h2>
				<p className="text-xs text-base-content/60">
					{t("livestock.weighing.rosterProgress", {
						done: doneCount,
						total,
					})}
				</p>
				{total > 0 && (
					<Progress
						value={doneCount}
						max={total}
						className="mt-2 h-1.5"
						aria-label={t("livestock.weighing.rosterProgress", {
							done: doneCount,
							total,
						})}
					/>
				)}
			</div>

			<div className="flex min-h-0 flex-1 flex-col border-t border-base-300">
				<RosterBody
					animals={animals}
					weighedByAnimalId={weighedByAnimalId}
					lockedAnimalId={lockedAnimalId}
					isLoading={isLoading}
					onPick={onPick}
				/>
			</div>
		</aside>
	);
}

function RosterGroup({
	label,
	count,
	children,
}: {
	label: string;
	count: number;
	children: ReactNode;
}) {
	return (
		<div className="mb-1">
			<div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-base-content/50">
				{label} · {count}
			</div>
			{children}
		</div>
	);
}
